import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  adminLogin, adminGetBusinesses, adminUpdateBusiness,
  adminDeleteBusiness, adminSync, adminSyncStatus, getStats,
  adminGetContacts, adminMarkContactRead, adminDeleteContact
} from '../api';
import {
  LogIn, LogOut, RefreshCw, Star, Eye, EyeOff, Trash2,
  Edit2, Check, X, ChevronLeft, ChevronRight, Search,
  BarChart2, List, Clock, Shield, Mail, MailOpen
} from 'lucide-react';

const ADMIN_TOKEN_KEY = 'mi_ispira_admin_token';

// ═══════════════════════════════════════════════════
// LOGIN
// ═══════════════════════════════════════════════════
function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('admin@mi-ispira.it');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await adminLogin(email, password);
      localStorage.setItem(ADMIN_TOKEN_KEY, res.data.token);
      onLogin(res.data.token);
      toast.success('Accesso effettuato');
    } catch (err) {
      setError(err.response?.data?.detail || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: '#0F0F10' }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-8"
        style={{ backgroundColor: '#1A1A1C', border: '1px solid rgba(255,255,255,0.08)' }}
        data-testid="admin-shell"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Shield size={24} style={{ color: 'var(--mi-accent)' }} />
          </div>
          <h1 className="font-display text-2xl mb-1" style={{ color: '#FAFAF8' }}>Admin</h1>
          <p className="text-sm" style={{ color: '#6B6560' }}>Mi-Ispira — Pannello di controllo</p>
        </div>
        <form data-testid="admin-login-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest block mb-1.5" style={{ color: '#9A9490' }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full h-11 px-4 rounded-xl text-sm"
              style={{ backgroundColor: '#2A2A2C', border: '1px solid rgba(255,255,255,0.1)', color: '#FAFAF8', outline: 'none' }}
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest block mb-1.5" style={{ color: '#9A9490' }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full h-11 px-4 rounded-xl text-sm"
              style={{ backgroundColor: '#2A2A2C', border: '1px solid rgba(255,255,255,0.1)', color: '#FAFAF8', outline: 'none' }}
              required
            />
          </div>
          {error && (
            <p className="text-sm text-red-400">{error}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit-button"
            disabled={loading}
            className="w-full h-11 rounded-xl text-sm font-medium transition-colors"
            style={{ backgroundColor: 'var(--mi-accent)', color: '#fff', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Accesso...' : 'Accedi'}
          </button>
        </form>
        <p className="text-xs text-center mt-4" style={{ color: '#6B6560' }}>Default: admin@mi-ispira.it / admin123</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// EDIT DIALOG
// ═══════════════════════════════════════════════════
function EditDialog({ business, onClose, onSave }) {
  const [form, setForm] = useState({
    payoff: business.payoff || '',
    target: business.target || '',
    motivi: (business.motivi || []).join('\n'),
    stato: business.stato || 'published',
    featured: business.featured || false,
    descrizione: business.descrizione || '',
    servizi: business.servizi || '',
    keywords: (business.keywords || []).join(', '),
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        payoff: form.payoff,
        target: form.target,
        motivi: form.motivi.split('\n').map(s => s.trim()).filter(Boolean),
        stato: form.stato,
        featured: form.featured,
        descrizione: form.descrizione,
        servizi: form.servizi,
        keywords: form.keywords.split(',').map(s => s.trim()).filter(Boolean),
      };
      const res = await adminUpdateBusiness(business._id, payload);
      onSave(res.data);
      toast.success('Attività aggiornata');
      onClose();
    } catch {
      toast.error('Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
    >
      <div
        className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#fff' }}
      >
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--mi-border)' }}>
          <h2 className="font-display text-xl" style={{ color: 'var(--mi-fg)' }}>Modifica: {business.nome}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest block mb-1" style={{ color: 'var(--mi-text)' }}>Stato</label>
              <select
                value={form.stato}
                onChange={e => setForm(p => ({ ...p, stato: e.target.value }))}
                className="w-full h-9 px-3 rounded-lg border text-sm"
                style={{ borderColor: 'var(--mi-border)' }}
              >
                <option value="published">Pubblicato</option>
                <option value="draft">Bozza</option>
                <option value="hidden">Nascosto</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={e => setForm(p => ({ ...p, featured: e.target.checked }))}
                  className="w-4 h-4"
                />
                <span style={{ color: 'var(--mi-fg)' }}>In evidenza</span>
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest block mb-1" style={{ color: 'var(--mi-text)' }}>Payoff (tagline breve)</label>
            <input
              value={form.payoff}
              onChange={e => setForm(p => ({ ...p, payoff: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border text-sm"
              style={{ borderColor: 'var(--mi-border)' }}
              placeholder="Breve frase ispirazionale..."
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest block mb-1" style={{ color: 'var(--mi-text)' }}>Target (per chi è adatta)</label>
            <input
              value={form.target}
              onChange={e => setForm(p => ({ ...p, target: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border text-sm"
              style={{ borderColor: 'var(--mi-border)' }}
              placeholder="Es: Privati, professionisti..."
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest block mb-1" style={{ color: 'var(--mi-text)' }}>Motivi per sceglierla (uno per riga)</label>
            <textarea
              value={form.motivi}
              onChange={e => setForm(p => ({ ...p, motivi: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
              style={{ borderColor: 'var(--mi-border)' }}
              placeholder="Qualità premium\nEsperienza 20+ anni\nAssistenza H24"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest block mb-1" style={{ color: 'var(--mi-text)' }}>Descrizione</label>
            <textarea
              value={form.descrizione}
              onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
              style={{ borderColor: 'var(--mi-border)' }}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest block mb-1" style={{ color: 'var(--mi-text)' }}>Servizi</label>
            <textarea
              value={form.servizi}
              onChange={e => setForm(p => ({ ...p, servizi: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
              style={{ borderColor: 'var(--mi-border)' }}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest block mb-1" style={{ color: 'var(--mi-text)' }}>Keywords (separati da virgola)</label>
            <input
              value={form.keywords}
              onChange={e => setForm(p => ({ ...p, keywords: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border text-sm"
              style={{ borderColor: 'var(--mi-border)' }}
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t" style={{ borderColor: 'var(--mi-border)' }}>
          <button
            onClick={onClose}
            className="px-4 h-9 rounded-lg border text-sm"
            style={{ borderColor: 'var(--mi-border)', color: 'var(--mi-fg)' }}
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-5 h-9 rounded-lg text-sm font-medium"
            style={{ backgroundColor: 'var(--mi-fg)', color: '#fff', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Salvataggio...' : 'Salva modifiche'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// MAIN ADMIN PANEL
// ═══════════════════════════════════════════════════
export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(ADMIN_TOKEN_KEY));
  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [filterStato, setFilterStato] = useState('');
  const [editBusiness, setEditBusiness] = useState(null);
  const [activeTab, setActiveTab] = useState('listings');
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsPages, setContactsPages] = useState(1);
  const [contactsPage, setContactsPage] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: 20 };
      if (searchQ) params.q = searchQ;
      if (filterStato) params.stato = filterStato;
      const [bizRes, statRes, syncRes] = await Promise.all([
        adminGetBusinesses(params),
        getStats(),
        adminSyncStatus(),
      ]);
      setBusinesses(bizRes.data.items);
      setTotal(bizRes.data.total);
      setPages(bizRes.data.pages);
      setStats(statRes.data);
      setSyncLogs(syncRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    setLoading(true);
    try {
      const res = await adminGetContacts({ page: contactsPage, limit: 20 });
      setContacts(res.data.items);
      setContactsTotal(res.data.total);
      setContactsPages(res.data.pages);
      setUnreadCount(res.data.unread);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentPage, filterStato]);

  useEffect(() => {
    if (token && activeTab === 'contacts') loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeTab, contactsPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadData();
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken(null);
    toast.success('Disconnesso');
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await adminSync();
      toast.success('Sincronizzazione avviata! Ricarica tra qualche secondo.');
      setTimeout(loadData, 5000);
    } catch {
      toast.error('Errore durante la sincronizzazione');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleFeatured = async (b) => {
    try {
      const res = await adminUpdateBusiness(b._id, { featured: !b.featured });
      setBusinesses(prev => prev.map(x => x._id === b._id ? res.data : x));
      toast.success(res.data.featured ? 'Messa in evidenza' : 'Rimossa dall\'evidenza');
    } catch {
      toast.error('Errore');
    }
  };

  const handleToggleStato = async (b) => {
    const newStato = b.stato === 'published' ? 'hidden' : 'published';
    try {
      const res = await adminUpdateBusiness(b._id, { stato: newStato });
      setBusinesses(prev => prev.map(x => x._id === b._id ? res.data : x));
      toast.success(newStato === 'published' ? 'Pubblicata' : 'Nascosta');
    } catch {
      toast.error('Errore');
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Eliminare “${b.nome}”?`)) return;
    try {
      await adminDeleteBusiness(b._id);
      setBusinesses(prev => prev.filter(x => x._id !== b._id));
      setTotal(t => t - 1);
      toast.success('Attività eliminata');
    } catch {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const handleSaved = (updated) => {
    setBusinesses(prev => prev.map(x => x._id === updated._id ? updated : x));
  };

  const handleMarkRead = async (c) => {
    try {
      await adminMarkContactRead(c._id);
      setContacts(prev => prev.map(x => x._id === c._id ? { ...x, letto: true } : x));
      setUnreadCount(u => Math.max(0, u - 1));
      toast.success('Segnato come letto');
    } catch {
      toast.error('Errore');
    }
  };

  const handleDeleteContact = async (c) => {
    if (!window.confirm('Eliminare questa richiesta?')) return;
    try {
      await adminDeleteContact(c._id);
      setContacts(prev => prev.filter(x => x._id !== c._id));
      setContactsTotal(t => t - 1);
      if (!c.letto) setUnreadCount(u => Math.max(0, u - 1));
      toast.success('Richiesta eliminata');
    } catch {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  if (!token) return <LoginForm onLogin={setToken} />;

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#0F0F10' }} data-testid="admin-shell">
      {/* Sidebar */}
      <div
        data-testid="admin-sidebar"
        className="w-60 flex-shrink-0 flex flex-col p-5 border-r"
        style={{ backgroundColor: '#121214', borderColor: 'rgba(255,255,255,0.07)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <span className="text-xl" style={{ color: 'var(--mi-accent)' }}>✦</span>
          <span className="font-display text-base" style={{ color: '#FAFAF8' }}>Mi-Ispira</span>
        </div>
        {/* Nav */}
        <nav className="space-y-1 flex-1">
          {[
            { id: 'dashboard', icon: BarChart2, label: 'Dashboard' },
            { id: 'listings', icon: List, label: 'Attività' },
            { id: 'contacts', icon: Mail, label: 'Richieste', badge: unreadCount },
            { id: 'sync', icon: Clock, label: 'Sincronizzazione' },
          ].map(({ id, icon: Icon, label, badge }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm transition-colors text-left relative"
              style={{
                backgroundColor: activeTab === id ? 'rgba(200,169,110,0.15)' : 'transparent',
                color: activeTab === id ? 'var(--mi-accent)' : '#9A9490',
              }}
            >
              <Icon size={16} />
              {label}
              {badge > 0 && (
                <span className="ml-auto text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: 'var(--mi-accent)', color: '#fff' }}>
                  {badge}
                </span>
              )}
            </button>
          ))}
        </nav>
        {/* Logout */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm mt-4"
          style={{ color: '#6B6560' }}
        >
          <LogOut size={14} /> Esci
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto" style={{ backgroundColor: '#FAFAF8' }}>
        <div className="p-8">

          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && (
            <div>
              <h1 className="font-display text-3xl mb-6" style={{ color: 'var(--mi-fg)' }}>Dashboard</h1>
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: 'Totale attività', value: stats.total_businesses, color: 'var(--mi-fg)' },
                    { label: 'In evidenza', value: stats.featured, color: 'var(--mi-accent)' },
                    { label: 'Categorie', value: stats.categories, color: 'var(--mi-fg)' },
                    { label: 'Ultimo sync', value: stats.last_sync?.timestamp ? new Date(stats.last_sync.timestamp).toLocaleDateString('it-IT') : 'Mai', color: 'var(--mi-fg)' },
                  ].map(({ label, value, color }) => (
                    <div
                      key={label}
                      className="rounded-2xl border p-5 bg-white"
                      style={{ borderColor: 'var(--mi-border)' }}
                    >
                      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--mi-text)' }}>{label}</p>
                      <p className="font-display text-3xl" style={{ color }}>{value}</p>
                    </div>
                  ))}
                </div>
              )}
              <button
                data-testid="admin-csv-sync-button"
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-2 px-5 h-10 rounded-xl text-sm font-medium transition-colors"
                style={{ backgroundColor: 'var(--mi-fg)', color: '#fff', opacity: syncing ? 0.7 : 1 }}
              >
                <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Sincronizzazione...' : 'Sincronizza CSV ora'}
              </button>
            </div>
          )}

          {/* LISTINGS TAB */}
          {activeTab === 'listings' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="font-display text-3xl" style={{ color: 'var(--mi-fg)' }}>Attività ({total})</h1>
                <button
                  data-testid="admin-csv-sync-button"
                  onClick={handleSync}
                  disabled={syncing}
                  className="flex items-center gap-2 px-4 h-9 rounded-xl text-sm font-medium border"
                  style={{ borderColor: 'var(--mi-border)', color: 'var(--mi-fg)', backgroundColor: '#fff' }}
                >
                  <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
                  Sync CSV
                </button>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-5">
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--mi-text)' }} />
                    <input
                      value={searchQ}
                      onChange={e => setSearchQ(e.target.value)}
                      placeholder="Cerca per nome..."
                      className="h-9 pl-9 pr-3 rounded-lg border text-sm w-56"
                      style={{ borderColor: 'var(--mi-border)', backgroundColor: '#fff' }}
                    />
                  </div>
                  <button type="submit" className="h-9 px-3 rounded-lg text-sm border" style={{ borderColor: 'var(--mi-border)' }}>Cerca</button>
                </form>
                <select
                  value={filterStato}
                  onChange={e => { setFilterStato(e.target.value); setCurrentPage(1); }}
                  className="h-9 px-3 rounded-lg border text-sm bg-white"
                  style={{ borderColor: 'var(--mi-border)' }}
                >
                  <option value="">Tutti gli stati</option>
                  <option value="published">Pubblicati</option>
                  <option value="draft">Bozze</option>
                  <option value="hidden">Nascosti</option>
                </select>
              </div>

              {/* Table */}
              <div
                data-testid="admin-listings-table"
                className="rounded-2xl border overflow-hidden bg-white"
                style={{ borderColor: 'var(--mi-border)' }}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--mi-border)', backgroundColor: 'var(--mi-muted)' }}>
                        <th className="text-left px-4 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Attività</th>
                        <th className="text-left px-4 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Categoria</th>
                        <th className="text-left px-4 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Città</th>
                        <th className="text-left px-4 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Stato</th>
                        <th className="text-left px-4 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        [...Array(5)].map((_, i) => (
                          <tr key={i}>
                            <td colSpan={5} className="px-4 py-3">
                              <div className="skeleton-shimmer h-4 rounded w-full" />
                            </td>
                          </tr>
                        ))
                      ) : businesses.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center" style={{ color: 'var(--mi-text)' }}>Nessuna attività trovata</td>
                        </tr>
                      ) : businesses.map((b) => (
                        <tr
                          key={b._id}
                          style={{ borderBottom: '1px solid var(--mi-border)' }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {b.logo ? (
                                <img src={b.logo} alt={b.nome} className="w-8 h-8 rounded-full object-contain border" style={{ borderColor: 'var(--mi-border)' }} />
                              ) : (
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs" style={{ backgroundColor: 'var(--mi-muted)', color: 'var(--mi-text)' }}>{b.nome?.[0]}</div>
                              )}
                              <div>
                                <p className="font-medium text-sm" style={{ color: 'var(--mi-fg)' }}>{b.nome}</p>
                                {b.featured && (
                                  <span className="text-xs" style={{ color: 'var(--mi-accent)' }}>★ In evidenza</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs" style={{ color: 'var(--mi-text)' }}>{b.categoria}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs" style={{ color: 'var(--mi-text)' }}>{b.citta}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className="text-xs px-2 py-1 rounded-full"
                              style={{
                                backgroundColor:
                                  b.stato === 'published' ? 'rgba(31,122,90,0.1)' :
                                  b.stato === 'draft' ? 'rgba(183,121,31,0.1)' :
                                  'rgba(180,35,24,0.1)',
                                color:
                                  b.stato === 'published' ? '#1F7A5A' :
                                  b.stato === 'draft' ? '#B7791F' : '#B42318',
                              }}
                            >
                              {b.stato === 'published' ? 'Pubblicato' : b.stato === 'draft' ? 'Bozza' : 'Nascosto'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setEditBusiness(b)}
                                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                                title="Modifica"
                              >
                                <Edit2 size={13} style={{ color: 'var(--mi-fg)' }} />
                              </button>
                              <button
                                onClick={() => handleToggleFeatured(b)}
                                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                                title={b.featured ? 'Rimuovi dall\'evidenza' : 'Metti in evidenza'}
                              >
                                <Star size={13} style={{ color: b.featured ? 'var(--mi-accent)' : 'var(--mi-text)', fill: b.featured ? 'var(--mi-accent)' : 'none' }} />
                              </button>
                              <button
                                onClick={() => handleToggleStato(b)}
                                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                                title={b.stato === 'published' ? 'Nascondi' : 'Pubblica'}
                              >
                                {b.stato === 'published' ? <Eye size={13} style={{ color: '#1F7A5A' }} /> : <EyeOff size={13} style={{ color: 'var(--mi-text)' }} />}
                              </button>
                              <button
                                onClick={() => handleDelete(b)}
                                className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                                title="Elimina"
                              >
                                <Trash2 size={13} style={{ color: '#B42318' }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    className="p-2 rounded-lg border disabled:opacity-40"
                    style={{ borderColor: 'var(--mi-border)' }}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm" style={{ color: 'var(--mi-text)' }}>Pagina {currentPage} di {pages}</span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(pages, p + 1))}
                    disabled={currentPage >= pages}
                    className="p-2 rounded-lg border disabled:opacity-40"
                    style={{ borderColor: 'var(--mi-border)' }}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* CONTACTS TAB */}
          {activeTab === 'contacts' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="font-display text-3xl" style={{ color: 'var(--mi-fg)' }}>Richieste di Contatto ({contactsTotal})</h1>
                  {unreadCount > 0 && (
                    <p className="text-sm mt-1" style={{ color: 'var(--mi-text)' }}>{unreadCount} non {unreadCount === 1 ? 'letta' : 'lette'}</p>
                  )}
                </div>
              </div>

              {/* Table */}
              <div
                data-testid="admin-contacts-table"
                className="rounded-2xl border overflow-hidden bg-white"
                style={{ borderColor: 'var(--mi-border)' }}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--mi-border)', backgroundColor: 'var(--mi-muted)' }}>
                        <th className="text-left px-4 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Stato</th>
                        <th className="text-left px-4 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Contatto</th>
                        <th className="text-left px-4 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Attività</th>
                        <th className="text-left px-4 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Messaggio</th>
                        <th className="text-left px-4 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Data</th>
                        <th className="text-left px-4 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        [...Array(5)].map((_, i) => (
                          <tr key={i}>
                            <td colSpan={6} className="px-4 py-3">
                              <div className="skeleton-shimmer h-4 rounded w-full" />
                            </td>
                          </tr>
                        ))
                      ) : contacts.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center" style={{ color: 'var(--mi-text)' }}>Nessuna richiesta</td>
                        </tr>
                      ) : contacts.map((c) => (
                        <tr
                          key={c._id}
                          style={{ borderBottom: '1px solid var(--mi-border)', backgroundColor: c.letto ? 'transparent' : 'rgba(200,169,110,0.05)' }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-3">
                            {c.letto ? (
                              <MailOpen size={16} style={{ color: 'var(--mi-text)' }} />
                            ) : (
                              <Mail size={16} style={{ color: 'var(--mi-accent)' }} />
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-sm" style={{ color: 'var(--mi-fg)' }}>{c.nome}</p>
                              <p className="text-xs" style={{ color: 'var(--mi-text)' }}>{c.email}</p>
                              {c.telefono && <p className="text-xs" style={{ color: 'var(--mi-text)' }}>{c.telefono}</p>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm font-medium" style={{ color: 'var(--mi-fg)' }}>{c.business_nome}</p>
                              {c.business_email && <p className="text-xs" style={{ color: 'var(--mi-text)' }}>{c.business_email}</p>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs line-clamp-2" style={{ color: 'var(--mi-text)' }}>{c.messaggio}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs whitespace-nowrap" style={{ color: 'var(--mi-text)' }}>
                              {new Date(c.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {!c.letto && (
                                <button
                                  onClick={() => handleMarkRead(c)}
                                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                                  title="Segna come letto"
                                >
                                  <Check size={13} style={{ color: '#1F7A5A' }} />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteContact(c)}
                                className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                                title="Elimina"
                              >
                                <Trash2 size={13} style={{ color: '#B42318' }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {contactsPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setContactsPage(p => Math.max(1, p - 1))}
                    disabled={contactsPage <= 1}
                    className="p-2 rounded-lg border disabled:opacity-40"
                    style={{ borderColor: 'var(--mi-border)' }}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm" style={{ color: 'var(--mi-text)' }}>Pagina {contactsPage} di {contactsPages}</span>
                  <button
                    onClick={() => setContactsPage(p => Math.min(contactsPages, p + 1))}
                    disabled={contactsPage >= contactsPages}
                    className="p-2 rounded-lg border disabled:opacity-40"
                    style={{ borderColor: 'var(--mi-border)' }}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* SYNC TAB */}
          {activeTab === 'sync' && (
            <div>
              <h1 className="font-display text-3xl mb-6" style={{ color: 'var(--mi-fg)' }}>Sincronizzazione CSV</h1>
              <div className="rounded-2xl border p-6 bg-white mb-6" style={{ borderColor: 'var(--mi-border)' }}>
                <p className="text-sm mb-4" style={{ color: 'var(--mi-text)' }}>
                  I dati vengono sincronizzati automaticamente dal Google Sheet ogni <strong>3 giorni</strong>.
                  Puoi anche avviare una sincronizzazione manuale.
                </p>
                <button
                  data-testid="admin-csv-sync-button"
                  onClick={handleSync}
                  disabled={syncing}
                  className="flex items-center gap-2 px-5 h-10 rounded-xl text-sm font-medium transition-colors"
                  style={{ backgroundColor: 'var(--mi-fg)', color: '#fff', opacity: syncing ? 0.7 : 1 }}
                >
                  <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
                  {syncing ? 'Sincronizzazione in corso...' : 'Avvia sincronizzazione'}
                </button>
              </div>

              {/* Sync logs */}
              {syncLogs.length > 0 && (
                <div className="rounded-2xl border overflow-hidden bg-white" style={{ borderColor: 'var(--mi-border)' }}>
                  <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--mi-border)', backgroundColor: 'var(--mi-muted)' }}>
                    <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--mi-text)' }}>Ultime sincronizzazioni</p>
                  </div>
                  <div className="divide-y" style={{ borderColor: 'var(--mi-border)' }}>
                    {syncLogs.map((log, i) => (
                      <div key={i} className="px-5 py-4 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--mi-fg)' }}>
                            {log.status === 'success' ? '\u2705 Successo' : '\u274c Errore'}
                          </p>
                          {log.stats && (
                            <p className="text-xs mt-0.5" style={{ color: 'var(--mi-text)' }}>
                              Aggiornate: {log.stats.updated || 0} | Saltate: {log.stats.skipped || 0}
                            </p>
                          )}
                          {log.error && (
                            <p className="text-xs mt-0.5 text-red-500">{log.error}</p>
                          )}
                        </div>
                        <p className="text-xs" style={{ color: 'var(--mi-text)' }}>
                          {log.timestamp ? new Date(log.timestamp).toLocaleString('it-IT') : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      {editBusiness && (
        <EditDialog
          business={editBusiness}
          onClose={() => setEditBusiness(null)}
          onSave={handleSaved}
        />
      )}
    </div>
  );
}
